<template>
  <div class="d-flex flex-column gap-4">
    <h1 class="sr-only">Retained page</h1>

    <Loader :is-processing="areRetainedAppointmentsLoading" />

    <!-- Filter Component -->
    <RetainedTableFilter
      :grade-years="gradeYears"
      :optional-date-ranges="optionalDateRanges"
      :active-table-tabs="activeTableTabs"
      :table-dropdowns="tableDropdowns"
      :is-tabular="state.isTabular"
      @search-updated="searchByQuery"
      @export-requested="handleExport"
      @column-options-changed="onColumnOptionsChange"
      @view-changed="handleViewChange"
      @retain-type-changed="handleRetainTypeChange"
      @grade-years-changed="(value) => setCalendarGradeYears(value)"
      @dates-changed="handleDatesChange"
      @clear-filter="resetState"
      @advanced-filter="advancedFilter"
    />

    <!-- Error Component -->
    <ErrorHandler
      v-if="serverError != null"
      :error="serverError"
      @done="serverError = null"
    />

    <!-- Table Component -->
    <RetainedTableCore
      :active-table-fields="activeTableFields"
      :retained-appointments="retainedAppointments"
      :pagination="pagination"
      :column-search-keys="state.columnSearchKeys"
      :is-tabular="state.isTabular"
      :is-lazy-loading-mode="state.isLazyLoadingMode"
      @column-sort="sortByColumn"
      @per-page-changed="setPerPage"
      @page-changed="setActivePage"
      @column-search="handleColumnSearch"
    />
  </div>
</template>

<script>
import { useGradeYears } from "@/composables/useGradeYears.js"
import download from "@/helpers/downloadCSV.js"
import helpers from "@/helpers/index.js"
import RetainedTableCore from "@/v3components/Datatables/RetainedTableCore.vue"
import RetainedTableFilter from "@/v3components/Datatables/RetainedTableFilter.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import moment from "moment"
import { computed, onMounted, onUnmounted, reactive, ref } from "vue"
import { isAllEntriesPerPageOption } from "@/constants/tablePaginationConstants.js"
import { attachMainLayoutLazyScroll } from "@/helpers/tableLazyScroll.js"
import { useToast } from "vue-toastification"
import { useStore } from "vuex"

const getDefaultColumnSearchKeys = () => {
  return {
    globalQuery: "",
    user: { value: "", column: ["user.name"], sortBy: "user.last_name" },
    recurrence_appointment_pass: {
      value: "",
      column: ["search_date_string"],
      sortBy: "for_date"
    },
    period: { value: "", column: ["period.name"], sortBy: "period.name" },
    from: { value: "", column: ["from.name"], sortBy: "from.name" },
    to: { value: "", column: ["to.name"], sortBy: "to.name" },
    created_by_user: {
      value: "",
      column: ["created_by_user.name"],
      sortBy: "created_by_user.name"
    },
    comments: {
      value: "",
      column: ["latest_comment.comment"],
      sortBy: "latest_comment.comment"
    }
  }
}

export default {
  name: "RetainedTable",
  components: {
    RetainedTableFilter,
    RetainedTableCore,
    Loader,
    ErrorHandler
  },
  setup() {
    const store = useStore()
    const toast = useToast()
    const { gradeYears, getMinDate, getMaxDate } = useGradeYears()

    const serverError = ref(null)

    const tableDropdowns = reactive({
      column: [
        { label: "Student", value: "user", checked: true },
        {
          label: "Date/Recurrence",
          value: "recurrence_appointment_pass",
          checked: true
        },
        { label: "Period", value: "period", checked: true },
        { label: "From", value: "from", checked: true },
        { label: "To", value: "to", checked: true },
        { label: "Creator", value: "created_by_user", checked: true },
        { label: "Comments", value: "comments", checked: true }
      ],
      tableView: [
        { label: "Basic View", value: false },
        { label: "Tabular View", value: true }
      ]
    })

    const state = reactive({
      isTabular: null,
      searchTimeOut: null,
      columnSearchKeys: getDefaultColumnSearchKeys(),
      isLazyLoadingMode: false
    })

    const activeTableTabs = [
      {
        label: "All Passes",
        value: "all_passes"
      }
    ]

    const activeTableFields = computed(() => {
      const allFields = [
        {
          key: "user",
          label: "Student",
          _style: "min-width:150px",
          type: "pass-view"
        },
        {
          key: "recurrence_appointment_pass",
          label: "Date/Recurrence",
          _style: "min-width:240px",
          type: "pass-view"
        },
        {
          key: "period",
          label: "Period",
          _style: "min-width:140px",
          type: "pass-view"
        },
        {
          key: "from",
          label: "From",
          type: "pass-view"
        },
        { key: "to", label: "To", type: "pass-view" },
        {
          key: "created_by_user",
          label: "Creator",
          type: "pass-view",
          _style: "min-width:170px"
        },
        { key: "comments", label: "Comments", _style: "min-width:200px" }
      ]

      return allFields.filter((field) => {
        const tableDropdownsWithChecked = tableDropdowns.column
          .filter((col) => col.checked)
          .map((col) => col.value)
        return tableDropdownsWithChecked.includes(field.key)
      })
    })

    const retainedAppointments = computed(() => {
      return store.getters["retained/retainedAppointments"]
    })

    const areRetainedAppointmentsLoading = computed(() => {
      return store.getters["retained/areRetainedAppointmentsLoading"]
    })

    const pagination = computed(() => {
      return store.getters["retained/pagination"]
    })

    const maxDate = computed(() => {
      return getMaxDate(store.state.retained.grade_years)
    })

    const minDate = computed(() => {
      return getMinDate(store.state.retained.grade_years)
    })

    const optionalDateRanges = computed(() => {
      const selectedSchoolYear = store.state.retained.grade_years?.value

      if (!selectedSchoolYear || !minDate.value) {
        return [
          {
            label: "Today",
            id: "",
            range: helpers.stringDateRange(moment(), moment())
          }
        ]
      }
      const [startYear, endYear] = selectedSchoolYear
      const today = moment()
      const currentMonth = today.month()
      const minSchoolDate = moment(helpers.minSchoolYearDate())

      const shouldUseStartYear =
        today.isAfter(minSchoolDate) && currentMonth >= 6 && currentMonth <= 11

      const selectedYear = shouldUseStartYear ? startYear : endYear

      const baseDate = moment({
        year: selectedYear,
        month: currentMonth,
        date: today.date()
      })

      const getRange = (days) => {
        const start = baseDate.clone().subtract(days, "days")
        const minSchoolYearDate = moment({
          year: minDate.value.getFullYear(),
          month: minDate.value.getMonth(),
          date: minDate.value.getDate()
        })
        const adjustedStart = start.isBefore(minSchoolYearDate)
          ? minSchoolYearDate
          : start

        return helpers.stringDateRange(adjustedStart, baseDate)
      }

      return [
        {
          label: "Today",
          id: "",
          range: helpers.stringDateRange(baseDate, baseDate)
        },
        {
          label: "Yesterday",
          id: "",
          range: helpers.stringDateRange(
            baseDate.clone().subtract(1, "days"),
            baseDate.clone().subtract(1, "days")
          )
        },
        {
          label: "Last 7 days",
          id: "",
          range: getRange(7)
        },
        {
          label: "Last 14 days",
          id: "",
          range: getRange(14)
        },
        {
          label: "Last 30 days",
          id: "",
          range: getRange(30)
        },
        {
          label: "This month",
          id: "",
          range: helpers.stringDateRange(
            baseDate.clone().startOf("month"),
            baseDate.clone().endOf("month")
          )
        },
        {
          label: "This year",
          id: "",
          range:
            helpers.currTzDate(minDate.value) +
            " - " +
            helpers.currTzDate(maxDate.value)
        },
        {
          label: "Custom range",
          range: null,
          startDate: helpers.currTzDate(minDate.value),
          endDate: helpers.currTzDate(maxDate.value),
          id: "custom"
        }
      ]
    })

    const onColumnOptionsChange = (selectedColumns) => {
      if (selectedColumns.length === 0) {
        tableDropdowns.column.forEach((tableColumn) => {
          tableColumn.checked = true
        })
        return
      }

      tableDropdowns.column.forEach((tableColumn) => {
        const matches = selectedColumns.includes(tableColumn.value)
        tableColumn.checked = matches
      })
    }

    const handleViewChange = (value) => {
      state.isTabular = value
    }

    const handleRetainTypeChange = (value) => {
      store.commit("retained/SET_RETAIN_TYPE", value)
    }

    const handleDatesChange = (value) => {
      const dates = value
        .split(" - ")
        .map((date) => moment(date, "MM/DD/YYYY").format("MM/DD/YYYY"))
      store.commit("retained/SET_BETWEEN_DATES", dates)
    }

    const handleColumnSearch = async ({ value, columnKey }) => {
      state.columnSearchKeys[columnKey].value = value
      await searchByQuery(value, "column")
    }

    const requestRetainedData = async (options = {}) => {
      const { page = null, isExport = false } = options

      if (page) {
        store.commit("retained/SET_ACTIVE_PAGE", page)
      }

      if (isExport) {
        await exportRetainedAppointments()
      } else {
        await fetchRetainedAppointments()
      }
    }

    const fetchRetainedAppointments = async (isLazyLoading = false) => {
      try {
        await store.dispatch("retained/fetchRetainedAppointments", {
          isLazyLoading
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching retained appointments:", error)
        toast.error("Failed to load retained appointments")
        serverError.value = error
      }
    }

    const exportRetainedAppointments = async () => {
      try {
        const response = await store.dispatch(
          "retained/exportRetainedAppointments"
        )

        if (response?.data) {
          download.CSVExport(response.data, "retained-appointments")
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error exporting retained appointments:", error)
        toast.error("Failed to export retained appointments")
        serverError.value = error
      }
    }

    const searchByQuery = async (value, type) => {
      clearInterval(state.searchTimeOut)

      state.searchTimeOut = setTimeout(async () => {
        await handleQuerySearch(value, type)
      }, 500)
    }

    const handleQuerySearch = async (value, type) => {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        store.commit("retained/SET_SEARCH_QUERY", "")
        store.commit("retained/SET_COLUMN_SEARCH_QUERY", "")
        if (type && type === "column") {
          state.columnSearchKeys.globalQuery = ""
          for (const key in state.columnSearchKeys) {
            if (Object.hasOwnProperty.call(state.columnSearchKeys, key)) {
              const columnData = state.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  const currentQuery = store.state.retained.column_search_query
                  store.commit(
                    "retained/SET_COLUMN_SEARCH_QUERY",
                    currentQuery + `${col}:${columnData.value}, `
                  )
                })
              }
            }
          }
          const currentQuery = store.state.retained.column_search_query
          store.commit(
            "retained/SET_COLUMN_SEARCH_QUERY",
            currentQuery.slice(0, -2)
          )
        } else {
          resetSearchQuery()
          state.columnSearchKeys.globalQuery = query
          store.commit("retained/SET_SEARCH_QUERY", query)
        }
        await fetchRetainedAppointments()
      }
    }

    const resetSearchQuery = () => {
      store.commit("retained/SET_SEARCH_QUERY", "")
      state.columnSearchKeys = getDefaultColumnSearchKeys()
    }

    const sortByColumn = async (column) => {
      const columnName = state.columnSearchKeys[column.column]
        ? state.columnSearchKeys[column.column].column[0]
        : column.column
      store.commit(
        "retained/SET_SORT_BY",
        `${columnName}:${column.asc ? "asc" : "desc"}`
      )
      await fetchRetainedAppointments()
    }

    const setPerPage = async (option) => {
      if (option) {
        state.isLazyLoadingMode = isAllEntriesPerPageOption(option)
        store.commit("retained/SET_PER_PAGE", option)
        await fetchRetainedAppointments()
        if (state.isLazyLoadingMode) {
          store.commit("retained/UPDATE_LAZY_LOADING_PAGINATION")
        }
      }
    }

    const setCalendarGradeYears = (gradeYear) => {
      store.commit("retained/SET_GRADE_YEARS", gradeYear)
      const dates = [
        helpers.currTzDate(minDate.value),
        helpers.currTzDate(maxDate.value)
      ]
      store.commit("retained/SET_BETWEEN_DATES", dates)
    }

    const setActivePage = async (page) => {
      store.commit("retained/SET_ACTIVE_PAGE", page)
      await fetchRetainedAppointments()
    }

    const handleExport = () => {
      requestRetainedData({
        page: store.state.retained.pagination.activePage,
        isExport: true
      })
    }

    const advancedFilter = async () => {
      await requestRetainedData()
    }

    const handleOnScroll = (body) => {
      if (
        state.isLazyLoadingMode &&
        retainedAppointments.value &&
        retainedAppointments.value.length &&
        pagination.value.total > retainedAppointments.value.length
      ) {
        if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
          const nextPage = pagination.value.activePage + 1
          store.commit("retained/SET_ACTIVE_PAGE", nextPage)
          fetchRetainedAppointments(true).then(() => {
            store.commit("retained/UPDATE_LAZY_LOADING_PAGINATION")
          })
        }
      }
    }

    let detachMainLayoutLazyScroll = null
    const initLazyLoading = () => {
      detachMainLayoutLazyScroll?.()
      detachMainLayoutLazyScroll = attachMainLayoutLazyScroll(() => {
        const body =
          document.querySelector(".main-scroll-container") ||
          document.querySelector(".c-body")
        if (!body) {
          return
        }
        handleOnScroll(body)
      })
    }

    const resetState = () => {
      store.commit("retained/RESET_STORE")
      if (gradeYears.value && gradeYears.value.length > 0) {
        store.commit(
          "retained/SET_GRADE_YEARS",
          gradeYears.value[gradeYears.value.length - 1]
        )
      }
      state.columnSearchKeys = getDefaultColumnSearchKeys()
      state.isLazyLoadingMode = false
      serverError.value = null
    }

    onMounted(() => {
      initLazyLoading()
      if (gradeYears.value && gradeYears.value.length > 0) {
        store.commit(
          "retained/SET_GRADE_YEARS",
          gradeYears.value[gradeYears.value.length - 1]
        )
      }
    })

    onUnmounted(() => {
      detachMainLayoutLazyScroll?.()
      resetState()
    })

    return {
      areRetainedAppointmentsLoading,
      pagination,
      state,
      activeTableTabs,
      tableDropdowns,
      activeTableFields,
      retainedAppointments,
      optionalDateRanges,
      gradeYears,
      serverError,
      onColumnOptionsChange,
      handleViewChange,
      handleRetainTypeChange,
      handleDatesChange,
      handleColumnSearch,
      requestRetainedData,
      searchByQuery,
      sortByColumn,
      setPerPage,
      setActivePage,
      handleExport,
      advancedFilter,
      resetState,
      setCalendarGradeYears,
      initLazyLoading
    }
  }
}
</script>
